import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported as fbIsSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase app (this is safe)
let app = null;
try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  console.log('Firebase app init error:', error.message);
}

// Messaging instance - initialized lazily
let messaging = null;
let messagingSupported = false;

// Check if browser supports notifications using Firebase's own check
const checkSupport = async () => {
  try {
    // First do basic checks
    if (typeof window === 'undefined') return false;
    if (!('Notification' in window)) return false;
    if (!('serviceWorker' in navigator)) return false;
    if (!('PushManager' in window)) return false;
    
    // Then check Firebase's own support
    const supported = await fbIsSupported();
    return supported;
  } catch (error) {
    console.log('Firebase support check failed:', error.message);
    return false;
  }
};

// Initialize messaging only when needed and supported
const initMessaging = async () => {
  if (messaging) return messaging;
  if (!app) return null;
  
  try {
    const supported = await checkSupport();
    if (!supported) {
      console.log('Firebase messaging not supported in this browser');
      return null;
    }
    
    messaging = getMessaging(app);
    messagingSupported = true;
    return messaging;
  } catch (error) {
    console.log('Firebase messaging init error:', error.message);
    return null;
  }
};

// Check if notifications are supported
export const isSupported = () => {
  if (typeof window === 'undefined') return false;
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
};

// Request notification permission and get FCM token
export const requestNotificationPermission = async () => {
  try {
    // Check basic support first
    if (!isSupported()) {
      console.log('Push notifications not supported in this browser');
      return null;
    }

    // Initialize messaging
    const msg = await initMessaging();
    if (!msg) {
      console.log('Could not initialize Firebase messaging');
      return null;
    }

    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      // Get FCM token
      const token = await getToken(msg, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
      });
      
      console.log('FCM Token obtained');
      return token;
    } else {
      console.log('Notification permission denied');
      return null;
    }
  } catch (error) {
    console.error('Error getting notification permission:', error.message);
    return null;
  }
};

// Listen for foreground messages
export const onMessageListener = () => {
  return new Promise(async (resolve, reject) => {
    try {
      const msg = await initMessaging();
      if (!msg) {
        reject('Messaging not supported');
        return;
      }
      
      onMessage(msg, (payload) => {
        console.log('Foreground message received:', payload);
        resolve(payload);
      });
    } catch (error) {
      reject(error.message);
    }
  });
};

export { messaging, messagingSupported };
